<script lang="ts">
  import AdminNav from '@/components/admin/AdminNav.svelte'
  import { router } from '@inertiajs/svelte'

  interface Post {
    id: string
    title: string
    board?: { name: string }
    user?: { name: string; email: string }
  }

  interface Review {
    id: string
    status: string
    report_count_at_creation: number
    created_at: string
    post?: Post
  }

  interface Pagination {
    current_page: number
    total_pages: number
  }

  interface Stats {
    pending_count?: number
    decided_count?: number
  }

  let {
    reviews = [] as Review[],
    pagination = {} as Pagination,
    filters = {} as Record<string, string>,
    stats = {} as Stats,
    flash = {} as Record<string, string>
  } = $props()

  // svelte-ignore state_referenced_locally
  const initialStatusFilter = filters.status || ''
  let statusFilter = $state(initialStatusFilter)

  function applyFilter() {
    router.get('/admin/content_reviews', { status: statusFilter }, { preserveState: true })
  }

  function getStatusInfo(status: string) {
    switch (status) {
      case 'pending':          return { badge: 'badge-warning', label: '심사 대기' }
      case 'kept':             return { badge: 'badge-success', label: '게시 유지' }
      case 'deleted_archived': return { badge: 'badge-error',   label: '삭제 보관' }
      default:                 return { badge: 'badge-neutral',  label: status }
    }
  }

  function goToPage(page: number) {
    router.get('/admin/content_reviews', { ...filters, page }, { preserveState: true })
  }
</script>

<AdminNav activeTab="content_reviews" />

  <main>
    {#if flash.notice}
      <div class="mb-6 p-4 bg-success/10 border border-success/30 rounded-xl text-success-dark dark:text-success text-body2">
        {flash.notice}
      </div>
    {/if}
    {#if flash.alert}
      <div class="mb-6 p-4 bg-error/10 border border-error/30 rounded-xl text-error text-body2">
        {flash.alert}
      </div>
    {/if}

    <!-- 헤더 + Stats -->
    <div class="flex items-center gap-6 mb-6 flex-wrap">
      <h2 class="text-h1 text-content">콘텐츠 심사</h2>
      <div class="flex items-center gap-3 text-body2">
        <span class="px-3 py-1 bg-warning/20 text-warning-dark dark:text-warning rounded-lg font-medium">
          심사 대기 {stats.pending_count || 0}건
        </span>
        <span class="px-3 py-1 bg-surface-tertiary text-content-secondary rounded-lg">
          처리 완료 {stats.decided_count || 0}건
        </span>
      </div>
    </div>

    <p class="text-body2 text-content-tertiary mb-6">
      신고 5건 이상 받은 게시물이 자동으로 임시 숨김 처리됩니다. 아래 목록에서 각 게시물을 검토하고 처리 방향을 결정해주세요.
    </p>

    <!-- 필터 -->
    <div class="card card-bordered p-4 mb-6 shadow-sm">
      <div class="flex items-center gap-4">
        <select bind:value={statusFilter} class="select">
          <option value="">전체</option>
          <option value="pending">심사 대기</option>
          <option value="kept">게시 유지</option>
          <option value="deleted_archived">삭제 보관</option>
        </select>
        <button onclick={applyFilter} class="btn btn-primary btn-md">검색</button>
      </div>
    </div>

    <!-- 목록 -->
    <div class="card card-bordered overflow-hidden shadow-sm">
      <table class="w-full">
        <thead class="bg-surface-tertiary">
          <tr>
            <th class="px-6 py-3 text-left text-caption font-semibold text-content-tertiary uppercase tracking-wide">게시물</th>
            <th class="px-6 py-3 text-left text-caption font-semibold text-content-tertiary uppercase tracking-wide">작성자</th>
            <th class="px-6 py-3 text-left text-caption font-semibold text-content-tertiary uppercase tracking-wide">신고 건수</th>
            <th class="px-6 py-3 text-left text-caption font-semibold text-content-tertiary uppercase tracking-wide">상태</th>
            <th class="px-6 py-3 text-left text-caption font-semibold text-content-tertiary uppercase tracking-wide">접수일</th>
            <th class="px-6 py-3"></th>
          </tr>
        </thead>
        <tbody class="divide-y divide-gray-100 dark:divide-dark-border">
          {#each reviews as review}
            {@const statusInfo = getStatusInfo(review.status)}
            <tr class="hover:bg-gray-50 dark:hover:bg-dark-tertiary transition-colors {review.status === 'pending' ? 'bg-warning/5' : ''}">
              <td class="px-6 py-4 max-w-xs">
                <p class="text-body1 font-medium text-content truncate">
                  {review.post?.title || '제목 없음'}
                </p>
                <p class="text-caption text-gray-400 dark:text-dark-text-disabled mt-0.5">
                  {review.post?.board?.name || ''}
                </p>
              </td>
              <td class="px-6 py-4">
                <p class="text-body2 text-gray-700 dark:text-dark-text-medium">
                  {review.post?.user?.name || '-'}
                </p>
                <p class="text-caption text-gray-400 dark:text-dark-text-disabled">{review.post?.user?.email || ''}</p>
              </td>
              <td class="px-6 py-4">
                <span class="inline-flex items-center gap-1 px-2.5 py-1 bg-error/10 text-error rounded-lg text-body2 font-bold">
                  {review.report_count_at_creation}건
                </span>
              </td>
              <td class="px-6 py-4">
                <span class={`badge ${statusInfo.badge}`}>{statusInfo.label}</span>
              </td>
              <td class="px-6 py-4 text-body2 text-content-tertiary">
                {new Date(review.created_at).toLocaleDateString('ko-KR')}
              </td>
              <td class="px-6 py-4 text-right">
                <a
                  href={`/admin/content_reviews/${review.id}`}
                  class="text-primary hover:text-primary-dark text-body2 font-medium transition-colors"
                >
                  {review.status === 'pending' ? '심사하기' : '상세보기'}
                </a>
              </td>
            </tr>
          {/each}
        </tbody>
      </table>

      {#if reviews.length === 0}
        <div class="text-center py-12">
          <svg class="w-12 h-12 text-gray-300 dark:text-dark-text-disabled mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
          </svg>
          <p class="text-body1 text-content-tertiary">심사할 콘텐츠가 없습니다</p>
        </div>
      {/if}
    </div>

    <!-- Pagination -->
    {#if pagination.total_pages > 1}
      <div class="flex items-center justify-center gap-2 mt-6">
        <button
          onclick={() => goToPage(pagination.current_page - 1)}
          disabled={pagination.current_page === 1}
          class="btn btn-secondary btn-sm disabled:opacity-50 disabled:cursor-not-allowed"
        >
          이전
        </button>
        <span class="px-4 py-2 text-body2 text-content-tertiary">
          {pagination.current_page} / {pagination.total_pages}
        </span>
        <button
          onclick={() => goToPage(pagination.current_page + 1)}
          disabled={pagination.current_page === pagination.total_pages}
          class="btn btn-secondary btn-sm disabled:opacity-50 disabled:cursor-not-allowed"
        >
          다음
        </button>
      </div>
    {/if}
  </main>
